import '../history/history-page.scss';
import './jetton-history-page.scss';

import NiceModal from '@ebay/nice-modal-react';
import { History, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { jettonApi } from '@/entities/jetton';
import { JettonHistoryItem } from '@/entities/jetton';
import { MODALS } from '@/shared/config/modals';
import { formatCurrencyBreakdown, formatUsd } from '@/shared/lib/currency';
import { getMultiplierTierClass } from '@/shared/lib/game/getMultiplierTierClass';
import { useTelegramBackButton } from '@/shared/lib/telegram/useTelegramBackButton';

const showRoundDetail = (roundId: string) =>
  NiceModal.show(MODALS.JETTON_ROUND_DETAIL, { roundId });

export const JettonHistoryPage = () => {
  useTelegramBackButton();

  const [items, setItems] = useState<JettonHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<JettonHistoryItem[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const observer = useRef<IntersectionObserver | null>(null);

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const result = await jettonApi.getHistory(p, 20);
      setItems((prev) => (p === 1 ? result.items : [...prev, ...result.items]));
      setHasMore(result.hasMore);
      setPage(p);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !searchValue) {
          loadPage(page + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, page, searchValue, loadPage],
  );

  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    const v = value.replace(/\D/g, '');
    setSearchValue(v);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await jettonApi.getHistory(1, 20);
        setSearchResults(result.items.filter((r) => String(r.index).includes(v)));
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const isSearching = searchValue.length > 0;
  const displayList = isSearching ? (searchResults ?? []) : items;
  const showSpinner = isSearching ? searchLoading : loading;
  const showEmpty = !showSpinner && displayList.length === 0;

  const getTierClass = (round: JettonHistoryItem) =>
    round?.crashMultiplier ? getMultiplierTierClass(round.crashMultiplier) : 'tier-base';

  return (
    <div className="history-page">
      <div className="history-page__header history-page__header--in">
        <History className="history-page__header-icon" />
        <h1 className="history-page__title">JetTON History</h1>
      </div>

      <div className="history-page__search">
        <Search className="history-page__search-icon" />
        <input
          className="history-page__search-input"
          type="text"
          inputMode="numeric"
          placeholder="Search by round #"
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchLoading && <Loader2 className="history-page__search-spinner" />}
      </div>

      {showSpinner && displayList.length === 0 ? (
        <div className="history-page__list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="history-page__skeleton jh-skeleton">
              <div className="jh-skeleton__mult" />
              <div className="history-page__skeleton-text">
                <div className="history-page__skeleton-line history-page__skeleton-line--wide" />
                <div className="history-page__skeleton-line history-page__skeleton-line--narrow" />
              </div>
            </div>
          ))}
        </div>
      ) : displayList.length > 0 ? (
        <div className="history-page__list">
          {displayList.map((round) => (
            <div
              key={round.id}
              className="history-page__item"
              onClick={() => showRoundDetail(round.id)}>
              <div className="history-page__item-left">
                <div className={`jh__mult-badge ${getTierClass(round)}`}>
                  {round.crashMultiplier.toFixed(2)}×
                </div>
                <div>
                  <div className="history-page__item-winner">Round #{round.index}</div>
                  <div className="history-page__item-meta">
                    {round.finishedAt ? new Date(round.finishedAt).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
              <span className="history-page__item-pot">
                {formatCurrencyBreakdown(round.totalsByCurrency)} · {formatUsd(round.totalBet)}
              </span>
            </div>
          ))}

          {!isSearching && hasMore && (
            <div ref={sentinelRef} className="history-page__sentinel">
              {loading && <Loader2 className="history-page__spinner" />}
            </div>
          )}
        </div>
      ) : showEmpty ? (
        <p className="history-page__empty history-page__empty--in">
          {isSearching ? 'No rounds found' : 'No rounds yet'}
        </p>
      ) : null}
    </div>
  );
};
